import { DatePicker } from "@/common/components/DatePicker";
import { ApiError } from "@/common/lib/api";
import { formatMoney } from "@/features/cost-allocations/utils/formatMoney";
import { isoLocalDate } from "@/features/cost-allocations/utils/formatPeriod";
import { useState } from "react";
import { useAccumulatedCost } from "../api/animalsApi";

type Props = {
  accountId: string;
  animalId: string;
};

export function AnimalAccumulatedCostSection({ accountId, animalId }: Props) {
  const [date, setDate] = useState<Date>(() => new Date());
  const atDate = isoLocalDate(date);
  const { data, isLoading, isFetching, isError, error } = useAccumulatedCost(
    accountId,
    animalId,
    atDate,
  );

  return (
    <section
      aria-label="Costo acumulado"
      className="rounded-2xl border border-app-border bg-app-surface p-4 md:p-5"
    >
      <h2 className="mb-3 font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
        Costo acumulado
      </h2>

      <div className="space-y-3">
        <div>
          <label
            htmlFor="accumulated-cost-date"
            className="mb-1.5 block font-medium text-[13px] text-app-ink-2"
          >
            Fecha de corte
          </label>
          <DatePicker
            id="accumulated-cost-date"
            value={date}
            onChange={(next) => {
              if (next) setDate(next);
            }}
            maxDate={new Date()}
            aria-label="Fecha de corte para el costo acumulado"
          />
        </div>

        <div className="rounded-xl border border-app-border bg-app-surface-2 p-3">
          <CostBody
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            error={error}
            value={data?.accumulatedCost}
            allocationCount={data?.allocationCount}
          />
        </div>
      </div>
    </section>
  );
}

type BodyProps = {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  value: string | undefined;
  allocationCount: number | undefined;
};

function CostBody({ isLoading, isFetching, isError, error, value, allocationCount }: BodyProps) {
  if (isLoading) {
    return <div className="h-7 w-32 animate-pulse rounded bg-app-border" />;
  }

  if (isError) {
    if (error instanceof ApiError && error.status === 403) {
      return (
        <p className="text-[13px] text-app-ink-3">No tenés permiso para ver el costo acumulado.</p>
      );
    }
    return <p className="text-[13px] text-app-ink-3">No se pudo calcular el costo.</p>;
  }

  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-semibold text-[20px] text-app-ink-1 tabular-nums">
        {formatMoney(value)}
      </span>
      <span className="text-[12px] text-app-ink-3">
        {allocationCount === 0
          ? "Sin asignaciones"
          : `${allocationCount} ${allocationCount === 1 ? "asignación" : "asignaciones"}`}
        {isFetching && !isLoading ? " · actualizando…" : ""}
      </span>
    </div>
  );
}
